import React from 'react';
import PropTypes from 'prop-types';
import {Navigate, useLocation} from 'react-router-dom';
import {Box, Typography, Button, Paper} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import {usePermissions} from '../../hooks/usePermissions';
import {useJWTAuth} from '../services/auth/jwt-auth/JWTAuthProvider';

/**
 * Access Denied component shown when user doesn't have required permissions
 */
const AccessDenied = ({message, onGoBack}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 500,
          textAlign: 'center',
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: 'error.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <LockIcon sx={{fontSize: 40, color: 'error.main'}} />
        </Box>

        <Typography variant='h5' gutterBottom fontWeight='bold'>
          Access Denied
        </Typography>

        <Typography variant='body1' color='text.secondary' sx={{mb: 2}}>
          {message || "You don't have permission to access this page."}
        </Typography>

        <Typography variant='body2' color='text.secondary' sx={{mb: 3}}>
          If you believe this is an error, try logging out and logging back in.
        </Typography>

        <Box display='flex' gap={2} justifyContent='center'>
          <Button variant='outlined' onClick={onGoBack}>
            Go Back
          </Button>
          <Button
            variant='contained'
            onClick={() => (window.location.href = '/signin')}
          >
            Login Again
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

AccessDenied.propTypes = {
  message: PropTypes.string,
  onGoBack: PropTypes.func.isRequired,
};

/**
 * ProtectedRoute component for role-based access control
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string[]} props.requiredRoles - Required roles to access this route
 * @param {string[]} props.requiredPermissions - Required permissions to access this route
 * @param {React.ReactNode} props.fallback - Custom fallback component
 */
const ProtectedRoute = ({
  children,
  requiredRoles = [],
  requiredPermissions = [],
  fallback = null,
}) => {
  const location = useLocation();
  const {user, isLoading} = useJWTAuth();
  const {
    isAuthenticated,
    hasRole,
    hasPermission,
    role: userRole,
  } = usePermissions();

  // Show loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '60vh',
        }}
      >
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to='/signin' state={{from: location}} replace />;
  }

  // Check role requirements
  const hasRequiredRole = requiredRoles.length === 0 || hasRole(requiredRoles);

  // Check permission requirements
  const hasRequiredPermission =
    requiredPermissions.length === 0 ||
    requiredPermissions.some((perm) => hasPermission(perm));

  // Access denied
  if (!hasRequiredRole || !hasRequiredPermission) {
    // Use custom fallback if provided
    if (fallback) {
      return fallback;
    }

    // Generate appropriate message
    let message = "You don't have permission to access this page.";
    if (!hasRequiredRole) {
      message = `This page requires ${requiredRoles.join(
        ' or ',
      )} role. Your current role is ${userRole}.`;
    } else if (!hasRequiredPermission) {
      message =
        "You don't have the required permissions to access this feature.";
    }

    return (
      <AccessDenied message={message} onGoBack={() => window.history.back()} />
    );
  }

  // All checks passed, render children
  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  requiredRoles: PropTypes.arrayOf(PropTypes.string),
  requiredPermissions: PropTypes.arrayOf(PropTypes.string),
  fallback: PropTypes.node,
};

export default ProtectedRoute;
